import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import '../styles/OwnerDashboard.css';

export default function OwnerCompanies() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Company creation now lives entirely on /owner/onboarding (the wizard) —
  // this page used to have its own duplicate inline create-form (posting to
  // POST /api/owner/onboard directly, see git history / OwnerCompanies.js.bak.*
  // for the removed version), which meant two independently-maintained copies
  // of the same "company_name/admin_name/.../referral_code/complimentary"
  // payload. Merged into one on 27 July 2026 — the "+ Add Company" button
  // below now just navigates to the wizard instead.
  const [subs, setSubs] = useState({}); // companyId -> subscription summary
  const [widgetLoading, setWidgetLoading] = useState({}); // companyId -> bool
  const [twoFaLoading, setTwoFaLoading] = useState({}); // companyId -> bool
  const [docInviteLoading, setDocInviteLoading] = useState({}); // companyId -> bool
  const [backbookLoading, setBackbookLoading] = useState({}); // companyId -> bool
  const [navModuleLoading, setNavModuleLoading] = useState({}); // `${companyId}:${module}` -> bool
  // Diary + Back Book setup wizards (19 Aug 2026) — per Tim's request, these two
  // modules need real configuration before they mean anything (a booking page +
  // advisers for Diary; an alert schedule for Back Book), so turning them "on" now
  // goes through a short guided flow instead of a blind toggle. Off-by-default for
  // every new company was already true at the DB level (broker_companies.nav_modules
  // column default) — this wizard is the missing UI half, not a defaults change.
  const [diaryWizard, setDiaryWizard] = useState(null); // { company, loading, error, slug, selectedIds, eligibleAdvisers, submitting }
  const [backbookWizard, setBackbookWizard] = useState(null); // { company, submitting, error }

  // Broker agreement editor (platform owner only — this page is already owner-gated).
  // Two independently-versioned tracks: 'standard' (paying brokers) and
  // 'complimentary' (founding members) — see migration 018.
  const [showAgreement, setShowAgreement] = useState(false);
  const [agreementAudience, setAgreementAudience] = useState('standard');
  const [agreement, setAgreement] = useState(null); // { id, version, title, content_md, created_at }
  const [agreementStats, setAgreementStats] = useState({ acceptedCount: 0, totalCompanies: 0 });
  const [agreementLoading, setAgreementLoading] = useState(false);
  const [agreementDraft, setAgreementDraft] = useState('');
  const [agreementTitle, setAgreementTitle] = useState('');
  const [agreementSaving, setAgreementSaving] = useState(false);
  const [agreementError, setAgreementError] = useState('');

  useEffect(() => {
    loadCompanies();
    // Poll for new companies every 5 seconds
    const interval = setInterval(loadCompanies, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadCompanies = async () => {
    try {
      const response = await fetch('/api/owner/companies', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCompanies(data.companies || []);
        setError('');
      } else {
        setError('Failed to load companies');
      }

      const subsRes = await fetch('/api/owner/subscriptions', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (subsRes.ok) {
        const subsData = await subsRes.json();
        const byId = {};
        (subsData.subscriptions || []).forEach(s => { byId[s.companyId] = s; });
        setSubs(byId);
      }
    } catch (err) {
      setError('Failed to load companies');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditTrial = async (companyId) => {
    const s = subs[companyId];
    const current = s && s.trialEndsAt ? new Date(s.trialEndsAt).toISOString().slice(0, 10) : '';
    const input = window.prompt(
      'Trial end date (YYYY-MM-DD). Leave empty to remove the trial:',
      current
    );
    if (input === null) return;
    const trimmed = input.trim();
    if (trimmed && !/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      setError('Invalid date — use YYYY-MM-DD');
      return;
    }
    try {
      const response = await fetch(`/api/owner/companies/${companyId}/subscription`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          trial_ends_at: trimmed ? `${trimmed}T23:59:59Z` : null,
          subscription_status: trimmed ? 'trialing' : 'active'
        })
      });
      if (response.ok) {
        setSuccess('Trial updated');
        loadCompanies();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const d = await response.json().catch(() => ({}));
        setError(d.error || 'Failed to update trial');
      }
    } catch (err) {
      setError('Error updating trial');
    }
  };

  const handleEditRate = async (companyId) => {
    const s = subs[companyId];
    const currentRate = s && s.seatRatePence != null ? (s.seatRatePence / 100).toFixed(2) : '';
    const input = window.prompt(
      `Custom rate per seat, in £ (e.g. 10.00). Leave empty to use the platform default rate` +
      (s ? ` (currently £${(s.seatRatePence / 100).toFixed(2)}/seat${s.isCustomRate ? ', custom' : ', default'})` : ''),
      currentRate
    );
    if (input === null) return;
    const trimmed = input.trim();
    let pence = null;
    if (trimmed) {
      const pounds = parseFloat(trimmed);
      if (!Number.isFinite(pounds) || pounds < 0) {
        setError('Invalid rate — enter a number like 10.00, or leave empty to clear');
        return;
      }
      pence = Math.round(pounds * 100);
    }
    try {
      const response = await fetch(`/api/owner/companies/${companyId}/rate`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ seat_rate_pence: pence })
      });
      if (response.ok) {
        setSuccess(pence === null ? 'Rate reset to platform default' : `Rate set to £${(pence / 100).toFixed(2)}/seat/month`);
        loadCompanies();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const d = await response.json().catch(() => ({}));
        setError(d.error || 'Failed to update rate');
      }
    } catch (err) {
      setError('Error updating rate');
    }
  };

  const loadAgreement = async (audience = agreementAudience) => {
    setAgreementLoading(true);
    setAgreementError('');
    try {
      const res = await fetch(`/api/owner/platform-agreement?audience=${audience}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Failed to load agreement');
      setAgreement(d.agreement);
      setAgreementDraft(d.agreement?.content_md || '');
      setAgreementTitle(d.agreement?.title || '');
      setAgreementStats({ acceptedCount: d.acceptedCount || 0, totalCompanies: d.totalCompanies || 0 });
    } catch (err) {
      setAgreementError(err.message);
    } finally {
      setAgreementLoading(false);
    }
  };

  const toggleAgreement = () => {
    const next = !showAgreement;
    setShowAgreement(next);
    if (next) loadAgreement(agreementAudience);
  };

  const switchAgreementAudience = (audience) => {
    if (audience === agreementAudience) return;
    setAgreementAudience(audience);
    loadAgreement(audience);
  };

  const handlePublishAgreement = async () => {
    if (!agreementDraft.trim()) {
      setAgreementError('Agreement text cannot be empty');
      return;
    }
    const audienceLabel = agreementAudience === 'complimentary' ? 'Founding Member (complimentary)' : 'Standard';
    const ok = window.confirm(
      `Publish a new ${audienceLabel} agreement version?\n\nAll ${agreementStats.totalCompanies} companies on this track will be prompted to re-accept the agreement next time they log in — including any who already accepted the current version.\n\nThis does not affect the other track's agreement.`
    );
    if (!ok) return;
    setAgreementSaving(true);
    setAgreementError('');
    try {
      const res = await fetch('/api/owner/platform-agreement', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content_md: agreementDraft, title: agreementTitle, audience: agreementAudience })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Failed to publish agreement');
      setAgreement(d.agreement);
      setSuccess(`${audienceLabel} agreement v${d.agreement.version} published — companies will be asked to re-accept.`);
      setTimeout(() => setSuccess(''), 5000);
      loadAgreement(agreementAudience);
      loadCompanies();
    } catch (err) {
      setAgreementError(err.message);
    } finally {
      setAgreementSaving(false);
    }
  };

  const SubBadge = ({ children, tone }) => {
    const tones = {
      green: { bg: '#d1fae5', color: '#065f46' },
      amber: { bg: '#fef3c7', color: '#92400e' },
      red:   { bg: '#fee2e2', color: '#991b1b' },
      grey:  { bg: '#f3f4f6', color: '#6b7280' },
    };
    const t = tones[tone] || tones.grey;
    return (
      <span style={{ display: 'inline-block', background: t.bg, color: t.color, borderRadius: '999px', padding: '2px 10px', fontSize: '12px', fontWeight: 600, marginRight: '4px', marginBottom: '2px', whiteSpace: 'nowrap' }}>
        {children}
      </span>
    );
  };

  const handleToggle2FA = async (company) => {
    const enabling = !company.require_2fa;
    const msg = enabling
      ? `Require 2FA for ${company.name}?\n\nAll brokers, introducers and partners at this company will be forced to set up two-factor authentication on next login.`
      : `Disable mandatory 2FA for ${company.name}?\n\nUsers at this company will be able to turn off their own 2FA. Only do this if a user is genuinely unable to use an authenticator app.`;
    if (!window.confirm(msg)) return;
    setTwoFaLoading(prev => ({ ...prev, [company.id]: true }));
    try {
      const response = await fetch(`/api/owner/companies/${company.id}/2fa`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: enabling }),
      });
      if (response.ok) {
        setSuccess(enabling ? '2FA enforcement enabled.' : '2FA enforcement disabled.');
        loadCompanies();
        setTimeout(() => setSuccess(''), 4000);
      } else {
        const d = await response.json().catch(() => ({}));
        setError(d.error || 'Failed to update 2FA setting');
      }
    } catch {
      setError('Failed to update 2FA setting');
    } finally {
      setTwoFaLoading(prev => ({ ...prev, [company.id]: false }));
    }
  };

  const handleToggleDocInvite = async (company) => {
    const enabling = !company.document_upload_invite_enabled;
    const msg = enabling
      ? `Enable "invite client to upload documents" for ${company.name}?\n\nBrokers at this company will be able to email their clients a secure link to upload requested documents.`
      : `Disable "invite client to upload documents" for ${company.name}?\n\nThe Invite Client button will be hidden and the API will reject new invites for this company (web and mobile).`;
    if (!window.confirm(msg)) return;
    setDocInviteLoading(prev => ({ ...prev, [company.id]: true }));
    try {
      const response = await fetch(`/api/owner/companies/${company.id}/document-upload-invite`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: enabling }),
      });
      if (response.ok) {
        setSuccess(enabling ? 'Document upload invites enabled.' : 'Document upload invites disabled.');
        loadCompanies();
        setTimeout(() => setSuccess(''), 4000);
      } else {
        const d = await response.json().catch(() => ({}));
        setError(d.error || 'Failed to update document upload invite setting');
      }
    } catch {
      setError('Failed to update document upload invite setting');
    } finally {
      setDocInviteLoading(prev => ({ ...prev, [company.id]: false }));
    }
  };

  const handleToggleBackbook = async (company) => {
    const enabling = !company.backbook_enabled;
    const msg = enabling
      ? `Enable Back Book for ${company.name}?

Gives this company a client renewal-tracking module (separate from the lead-gen pipeline), with an 8/6/3-month alert schedule seeded automatically.`
      : `Disable Back Book for ${company.name}?

The Back Book nav item and API will be hidden/blocked for this company. Existing back-book data is kept, not deleted.`;
    if (!window.confirm(msg)) return;
    setBackbookLoading(prev => ({ ...prev, [company.id]: true }));
    try {
      const response = await fetch(`/api/owner/companies/${company.id}/backbook`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: enabling }),
      });
      if (response.ok) {
        setSuccess(enabling ? 'Back Book enabled.' : 'Back Book disabled.');
        loadCompanies();
        setTimeout(() => setSuccess(''), 4000);
      } else {
        const d = await response.json().catch(() => ({}));
        setError(d.error || 'Failed to update Back Book setting');
      }
    } catch {
      setError('Failed to update Back Book setting');
    } finally {
      setBackbookLoading(prev => ({ ...prev, [company.id]: false }));
    }
  };

  // Leads/Deals/Introducers — generic per-company module toggle (14 Aug 2026, built
  // for Alegra who want Back Book only, no lead-gen/pipeline side at all). Same
  // confirm-then-PATCH-then-reload pattern as Back Book above; the real enforcement
  // (nav hide + API 403) lives server-side, this is just the owner-facing switch.
  const MODULE_LABELS = { leads: 'Leads', deals: 'Deals', introducers: 'Introducers', clients: 'Clients', analytics: 'Analytics', announcements: 'Announcements', scheduling: 'Scheduling' };
  const handleToggleNavModule = async (company, moduleKey) => {
    const current = company.nav_modules?.[moduleKey] !== false; // default true if unset
    const enabling = !current;
    const label = MODULE_LABELS[moduleKey];
    const msg = enabling
      ? `Turn ${label} back on for ${company.name}?\n\nThe ${label} nav item and API become reachable again immediately.`
      : `Turn ${label} off for ${company.name}?\n\nHides the ${label} nav item and blocks its API for every user on this company (403). Existing ${label.toLowerCase()} data is kept, not deleted.`;
    if (!window.confirm(msg)) return;
    const loadingKey = `${company.id}:${moduleKey}`;
    setNavModuleLoading(prev => ({ ...prev, [loadingKey]: true }));
    try {
      const response = await fetch(`/api/owner/companies/${company.id}/nav-modules`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ modules: { [moduleKey]: enabling } }),
      });
      if (response.ok) {
        setSuccess(`${label} ${enabling ? 'enabled' : 'disabled'} for ${company.name}.`);
        loadCompanies();
        setTimeout(() => setSuccess(''), 4000);
      } else {
        const d = await response.json().catch(() => ({}));
        setError(d.error || `Failed to update ${label} setting`);
      }
    } catch {
      setError(`Failed to update ${label} setting`);
    } finally {
      setNavModuleLoading(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  // Back Book wizard — a short explainer + confirm before the existing enable
  // endpoint runs (that endpoint already auto-seeds the 8/6/3/2/1-month alert
  // schedule + escalating-urgency email templates, so there's genuinely nothing
  // more to configure here — the wizard's job is just making that visible before
  // Tim commits, not collecting more input).
  const openBackbookWizard = (company) => setBackbookWizard({ company, submitting: false, error: '' });
  const closeBackbookWizard = () => setBackbookWizard(null);
  const submitBackbookWizard = async () => {
    if (!backbookWizard) return;
    const company = backbookWizard.company;
    setBackbookWizard(prev => ({ ...prev, submitting: true, error: '' }));
    try {
      const response = await fetch(`/api/owner/companies/${company.id}/backbook`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: true }),
      });
      if (response.ok) {
        setSuccess('Back Book set up and enabled.');
        closeBackbookWizard();
        loadCompanies();
        setTimeout(() => setSuccess(''), 4000);
      } else {
        const d = await response.json().catch(() => ({}));
        setBackbookWizard(prev => ({ ...prev, submitting: false, error: d.error || 'Failed to enable Back Book' }));
      }
    } catch {
      setBackbookWizard(prev => ({ ...prev, submitting: false, error: 'Failed to enable Back Book' }));
    }
  };

  // Diary wizard — creates the company's one general booking page (slug + which
  // already-calendar-connected advisers go on it) and flips nav_modules.scheduling
  // on in one step. Advisers who haven't connected a calendar yet (Settings, self-
  // service, same as every other company) show up greyed out with a note — they get
  // added afterward, either by themselves or by revisiting this same page's Modules
  // tools, once connected. That's the real reason Diary can't just be a blind toggle:
  // a page with a mode and a slug is meaningful state, not a boolean.
  const slugify = (name) => (name || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 63) || 'company';
  const openDiaryWizard = async (company) => {
    setDiaryWizard({ company, loading: true, error: '', slug: slugify(company.name), selectedIds: [], eligibleAdvisers: [], submitting: false });
    try {
      const response = await fetch(`/api/owner/companies/${company.id}/diary-setup`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const d = await response.json();
      if (!response.ok) throw new Error(d.error || 'Failed to load Diary setup status');
      setDiaryWizard(prev => ({ ...prev, loading: false, eligibleAdvisers: d.eligibleAdvisers || [] }));
    } catch (err) {
      setDiaryWizard(prev => ({ ...prev, loading: false, error: err.message }));
    }
  };
  const closeDiaryWizard = () => setDiaryWizard(null);
  const toggleDiaryAdviser = (userId) => {
    setDiaryWizard(prev => ({
      ...prev,
      selectedIds: prev.selectedIds.includes(userId)
        ? prev.selectedIds.filter(id => id !== userId)
        : [...prev.selectedIds, userId],
    }));
  };
  const submitDiaryWizard = async () => {
    if (!diaryWizard) return;
    const { company, slug, selectedIds } = diaryWizard;
    if (!slug || !/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(slug)) {
      setDiaryWizard(prev => ({ ...prev, error: 'Slug must be lowercase letters, numbers and hyphens only.' }));
      return;
    }
    setDiaryWizard(prev => ({ ...prev, submitting: true, error: '' }));
    try {
      const response = await fetch(`/api/owner/companies/${company.id}/diary-setup`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, adviserUserIds: selectedIds }),
      });
      const d = await response.json();
      if (!response.ok) throw new Error(d.error || 'Failed to set up Diary');
      const skipped = selectedIds.length - (d.advisers?.length || 0);
      setSuccess(`Diary set up for ${company.name}` + (skipped > 0 ? ` (${skipped} selected adviser${skipped === 1 ? '' : 's'} skipped — no calendar connected).` : '.'));
      closeDiaryWizard();
      loadCompanies();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setDiaryWizard(prev => ({ ...prev, submitting: false, error: err.message }));
    }
  };

  const handleToggleWidget = async (company) => {
    const enabling = !company.widget_enabled;
    const confirm = enabling
      ? window.confirm(`Enable website widget for ${company.name}?\n\nThis generates a unique API key and gives them access to the widget feature.`)
      : window.confirm(`Disable website widget for ${company.name}?\n\nTheir embed code will stop working immediately.`);
    if (!confirm) return;

    setWidgetLoading(prev => ({ ...prev, [company.id]: true }));
    try {
      const response = await fetch(`/api/owner/companies/${company.id}/widget`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled: enabling }),
      });
      if (response.ok) {
        setSuccess(enabling ? 'Widget enabled — API key generated.' : 'Widget disabled.');
        loadCompanies();
        setTimeout(() => setSuccess(''), 4000);
      } else {
        const d = await response.json().catch(() => ({}));
        setError(d.error || 'Failed to update widget');
      }
    } catch {
      setError('Failed to update widget');
    } finally {
      setWidgetLoading(prev => ({ ...prev, [company.id]: false }));
    }
  };

  // Agreement live/not-live status is now read-only here (7 July) — pushing
  // a specific template live happens per-row on the Agreements page instead,
  // since a company-wide toggle with no template attached was confusing
  // (Tim: "not added to a specific agreement so it's a bit confusing what
  // will be pushed"). "Manage" below still opens that page.

  const handleDeleteCompany = async (companyId) => {
    if (!window.confirm('Are you sure you want to delete this company?')) return;

    try {
      const response = await fetch(`/api/companies/${companyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSuccess('Company deleted successfully');
        loadCompanies();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to delete company');
      }
    } catch (err) {
      setError('Error deleting company');
      console.error(err);
    }
  };

  return (
    <>
      <div className="dashboard-container">
        <Sidebar />
        <main className="dashboard-main">
          <header className="dashboard-header">
            <h1>Manage Companies</h1>
            <p>Create and manage broker companies</p>
          </header>

          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          {backbookWizard && (
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '24px', width: '440px', maxWidth: '90vw' }}>
                <h2 style={{ marginTop: 0 }}>Set up Back Book — {backbookWizard.company.name}</h2>
                <p style={{ color: '#4b5563', fontSize: '14px' }}>This will:</p>
                <ul style={{ color: '#4b5563', fontSize: '14px', paddingLeft: '20px' }}>
                  <li>Turn on the Back Book nav item and API for this company</li>
                  <li>Seed a default renewal alert schedule (8 / 6 / 3 / 2 / 1 months before end date)</li>
                  <li>Create editable email templates for each alert stage</li>
                </ul>
                <p style={{ color: '#6b7280', fontSize: '13px' }}>The schedule and templates can be edited afterward from the company's own Settings → Back Book Preferences.</p>
                {backbookWizard.error && <div className="alert alert-error">{backbookWizard.error}</div>}
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <button onClick={closeBackbookWizard} disabled={backbookWizard.submitting}
                    style={{ padding: '8px 16px', backgroundColor: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                    Cancel
                  </button>
                  <button onClick={submitBackbookWizard} disabled={backbookWizard.submitting}
                    style={{ padding: '8px 16px', backgroundColor: '#10b981', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                    {backbookWizard.submitting ? 'Setting up…' : 'Set Up & Enable'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {diaryWizard && (
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '24px', width: '480px', maxWidth: '90vw', maxHeight: '85vh', overflowY: 'auto' }}>
                <h2 style={{ marginTop: 0 }}>Set up Diary — {diaryWizard.company.name}</h2>
                {diaryWizard.loading ? (
                  <p>Loading…</p>
                ) : (
                  <>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Booking page slug</label>
                    <input
                      type="text"
                      value={diaryWizard.slug}
                      onChange={(e) => setDiaryWizard(prev => ({ ...prev, slug: e.target.value.toLowerCase() }))}
                      style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', marginBottom: '4px', boxSizing: 'border-box' }}
                    />
                    <p style={{ color: '#6b7280', fontSize: '12px', marginTop: 0 }}>Public page will be at brokerconnect.co.uk/book/{diaryWizard.slug || '…'}</p>

                    <p style={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Who's on the booking page?</p>
                    {diaryWizard.eligibleAdvisers.length === 0 ? (
                      <p style={{ color: '#6b7280', fontSize: '13px' }}>No team members yet — you can still create the page now and add people once they've been invited and connected a calendar.</p>
                    ) : (
                      <div style={{ border: '1px solid #e5e7eb', borderRadius: '4px', padding: '8px', marginBottom: '8px' }}>
                        {diaryWizard.eligibleAdvisers.map(a => (
                          <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', fontSize: '13px', color: a.has_calendar ? '#111827' : '#9ca3af' }}>
                            <input
                              type="checkbox"
                              disabled={!a.has_calendar}
                              checked={diaryWizard.selectedIds.includes(a.id)}
                              onChange={() => toggleDiaryAdviser(a.id)}
                            />
                            {a.name} {!a.has_calendar && <span>— needs to connect their calendar in Settings first</span>}
                          </label>
                        ))}
                      </div>
                    )}
                    <p style={{ color: '#6b7280', fontSize: '12px' }}>Anyone not selected (or without a connected calendar yet) can be added later — this just turns Diary on and creates the page.</p>
                  </>
                )}
                {diaryWizard.error && <div className="alert alert-error">{diaryWizard.error}</div>}
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <button onClick={closeDiaryWizard} disabled={diaryWizard.submitting}
                    style={{ padding: '8px 16px', backgroundColor: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                    Cancel
                  </button>
                  <button onClick={submitDiaryWizard} disabled={diaryWizard.loading || diaryWizard.submitting}
                    style={{ padding: '8px 16px', backgroundColor: '#10b981', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                    {diaryWizard.submitting ? 'Setting up…' : 'Set Up & Enable'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
            <button
              onClick={() => navigate('/owner/onboarding')}
              style={{
                padding: '10px 16px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              + Add Company
            </button>
            <button 
              onClick={loadCompanies}
              style={{
                padding: '10px 16px',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
              title="Refresh company list"
            >
              ↻ Refresh
            </button>
            <button
              onClick={toggleAgreement}
              style={{
                padding: '10px 16px',
                backgroundColor: showAgreement ? '#374151' : '#8b5cf6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              {showAgreement ? '✕ Close Agreement Editor' : '📄 Broker Agreement'}
            </button>
          </div>

          {showAgreement && (
            <div style={{
              padding: '20px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{ marginBottom: '4px' }}>Broker Onboarding Agreement</h3>
              <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '12px' }}>
                Two independently-versioned tracks. Standard is what every paying broker company must
                accept (Fees, GDPR roles, termination, etc.). Complimentary is the founding-member
                version used automatically for companies onboarded with the "Complimentary" checkbox
                — its Fees clause is replaced with no-billing wording instead. Publishing a change only
                affects the track you're viewing; companies on that track will be asked to re-accept.
              </p>

              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <button
                  type="button"
                  onClick={() => switchAgreementAudience('standard')}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '6px',
                    border: agreementAudience === 'standard' ? '2px solid #8b5cf6' : '1px solid #ddd',
                    backgroundColor: agreementAudience === 'standard' ? '#ede9fe' : '#fff',
                    color: '#374151',
                    fontWeight: agreementAudience === 'standard' ? 600 : 400,
                    cursor: 'pointer'
                  }}
                >
                  Standard
                </button>
                <button
                  type="button"
                  onClick={() => switchAgreementAudience('complimentary')}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '6px',
                    border: agreementAudience === 'complimentary' ? '2px solid #10b981' : '1px solid #ddd',
                    backgroundColor: agreementAudience === 'complimentary' ? '#d1fae5' : '#fff',
                    color: '#374151',
                    fontWeight: agreementAudience === 'complimentary' ? 600 : 400,
                    cursor: 'pointer'
                  }}
                >
                  Complimentary (Founding Members)
                </button>
              </div>

              {agreementError && <div className="alert alert-error" style={{ marginBottom: '12px' }}>{agreementError}</div>}

              {agreementLoading ? (
                <p>Loading current agreement…</p>
              ) : agreement ? (
                <>
                  <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', fontSize: '13px', color: '#374151' }}>
                    <span><strong>Current version:</strong> {agreement.version}</span>
                    <span><strong>Published:</strong> {new Date(agreement.created_at).toLocaleString()}</span>
                    <span><strong>Accepted:</strong> {agreementStats.acceptedCount} / {agreementStats.totalCompanies} companies</span>
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '13px' }}>Title</label>
                    <input
                      type="text"
                      value={agreementTitle}
                      onChange={(e) => setAgreementTitle(e.target.value)}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '13px' }}>
                      Agreement text (Markdown)
                    </label>
                    <textarea
                      value={agreementDraft}
                      onChange={(e) => setAgreementDraft(e.target.value)}
                      rows={18}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontFamily: 'monospace',
                        fontSize: '13px',
                        lineHeight: '1.5',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      onClick={handlePublishAgreement}
                      disabled={agreementSaving}
                      style={{
                        padding: '10px 16px',
                        backgroundColor: '#8b5cf6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: agreementSaving ? 'default' : 'pointer',
                        opacity: agreementSaving ? 0.7 : 1,
                      }}
                    >
                      {agreementSaving ? 'Publishing…' : `Publish as v${(() => {
                        const parts = String(agreement.version).split('.');
                        const minor = parseInt(parts[1] || '0', 10);
                        return `${parts[0]}.${Number.isFinite(minor) ? minor + 1 : 1}`;
                      })()}`}
                    </button>
                    <button
                      onClick={() => { setAgreementDraft(agreement.content_md); setAgreementTitle(agreement.title); setAgreementError(''); }}
                      disabled={agreementSaving}
                      style={{
                        padding: '10px 16px',
                        backgroundColor: '#6b7280',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      Reset changes
                    </button>
                  </div>
                </>
              ) : (
                <p>No active agreement found.</p>
              )}
            </div>
          )}

          {loading ? (
            <p>Loading companies...</p>
          ) : companies.length === 0 ? (
            <p>No companies found</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #ddd' }}>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Name</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Plan</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Users</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Monthly Fee</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Created</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Widget</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>2FA</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Doc Upload Invite</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Back Book</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Modules</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Introducer Agreements</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map(company => {
                    const s = subs[company.id];
                    return (
                    <tr key={company.id} style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '12px' }}>
                        <button
                          onClick={() => navigate(`/owner/company/${company.id}`)}
                          style={{ color: '#3b82f6', cursor: 'pointer', border: 'none', background: 'none' }}
                        >
                          {company.name}
                        </button>
                      </td>
                      <td style={{ padding: '12px' }}>{s ? s.tierLabel : company.subscription_tier}</td>
                      <td style={{ padding: '12px' }}>
                        {s ? `${s.userCount}${s.maxUsers ? ` / ${s.maxUsers}` : ''}` : '-'}
                      </td>
                      <td style={{ padding: '12px' }}>
                        {s ? (s.onTrial
                          ? <span title={`£${s.monthlyFee.toFixed(2)} after trial`}>£0.00 <span style={{ color: '#9ca3af', fontSize: '12px' }}>(£{s.monthlyFee.toFixed(2)})</span></span>
                          : `£${s.monthlyFee.toFixed(2)}`) : '-'}
                        {s && s.isCustomRate && (
                          <div title="This company has a custom per-seat rate, different from the platform default">
                            <SubBadge tone="grey">£{(s.seatRatePence / 100).toFixed(2)}/seat custom</SubBadge>
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '12px' }}>
                        {s ? (
                          <>
                            {s.onTrial && (
                              <SubBadge tone={s.trialEndingSoon ? 'amber' : 'green'}>
                                Trial · {s.trialDaysLeft}d left
                              </SubBadge>
                            )}
                            {s.trialExpired && <SubBadge tone="red">Trial ended — billing due</SubBadge>}
                            {s.overLimit && <SubBadge tone="red">Over user limit</SubBadge>}
                            {!s.agreementAccepted && <SubBadge tone="amber">Agreement pending</SubBadge>}
                            {!s.onTrial && !s.trialExpired && !s.overLimit && s.agreementAccepted && (
                              <SubBadge tone="green">Active</SubBadge>
                            )}
                            {s.agreementAccepted && s.agreementAcceptedAt && (
                              <span title={`Accepted by ${s.agreementAcceptedBy || 'unknown'} on ${new Date(s.agreementAcceptedAt).toLocaleDateString()}`}
                                style={{ fontSize: '12px', color: '#9ca3af', cursor: 'help' }}> ✓ v{s.agreementVersion}</span>
                            )}
                          </>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '12px' }}>{new Date(company.created_at).toLocaleDateString()}</td>
                      <td style={{ padding: '12px' }}>
                        <button
                          onClick={() => handleToggleWidget(company)}
                          disabled={widgetLoading[company.id]}
                          style={{
                            padding: '5px 12px',
                            backgroundColor: company.widget_enabled ? '#10b981' : '#e5e7eb',
                            color: company.widget_enabled ? '#fff' : '#374151',
                            border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                          }}
                          title={company.widget_enabled ? `Enabled · API key: ${company.widget_api_key}` : 'Click to enable'}
                        >
                          {widgetLoading[company.id] ? '…' : (company.widget_enabled ? '✓ On' : 'Off')}
                        </button>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <button
                          onClick={() => handleToggle2FA(company)}
                          disabled={twoFaLoading[company.id]}
                          style={{
                            padding: '5px 12px',
                            backgroundColor: company.require_2fa ? '#6366f1' : '#e5e7eb',
                            color: company.require_2fa ? '#fff' : '#374151',
                            border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                          }}
                          title={company.require_2fa ? 'Mandatory 2FA on — click to disable' : 'Mandatory 2FA off — click to enable'}
                        >
                          {twoFaLoading[company.id] ? '…' : (company.require_2fa ? '🔒 On' : 'Off')}
                        </button>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <button
                          onClick={() => handleToggleDocInvite(company)}
                          disabled={docInviteLoading[company.id]}
                          style={{
                            padding: '5px 12px',
                            backgroundColor: company.document_upload_invite_enabled ? '#10b981' : '#e5e7eb',
                            color: company.document_upload_invite_enabled ? '#fff' : '#374151',
                            border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                          }}
                          title={company.document_upload_invite_enabled ? 'Enabled — click to disable' : 'Disabled — click to enable'}
                        >
                          {docInviteLoading[company.id] ? '…' : (company.document_upload_invite_enabled ? '✓ On' : 'Off')}
                        </button>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <button
                          onClick={() => company.backbook_enabled ? handleToggleBackbook(company) : openBackbookWizard(company)}
                          disabled={backbookLoading[company.id]}
                          style={{
                            padding: '5px 12px',
                            backgroundColor: company.backbook_enabled ? '#10b981' : '#e5e7eb',
                            color: company.backbook_enabled ? '#fff' : '#374151',
                            border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                          }}
                          title={company.backbook_enabled ? 'Enabled — click to disable' : 'Disabled — click to set up & enable'}
                        >
                          {backbookLoading[company.id] ? '…' : (company.backbook_enabled ? '✓ On' : 'Set Up')}
                        </button>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {['leads', 'deals', 'introducers', 'clients', 'analytics', 'announcements', 'scheduling'].map(moduleKey => {
                            const on = company.nav_modules?.[moduleKey] !== false;
                            const loadingKey = `${company.id}:${moduleKey}`;
                            return (
                              <button
                                key={moduleKey}
                                onClick={() => (moduleKey === 'scheduling' && !on) ? openDiaryWizard(company) : handleToggleNavModule(company, moduleKey)}
                                disabled={navModuleLoading[loadingKey]}
                                style={{
                                  padding: '3px 8px',
                                  backgroundColor: on ? '#10b981' : '#e5e7eb',
                                  color: on ? '#fff' : '#374151',
                                  border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 600,
                                }}
                                title={moduleKey === 'scheduling' && !on ? 'Off — click to set up Diary' : `${MODULE_LABELS[moduleKey]}: ${on ? 'On — click to switch off' : 'Off — click to switch on'}`}
                              >
                                {navModuleLoading[loadingKey] ? '…' : (moduleKey === 'scheduling' && !on ? 'Set Up' : MODULE_LABELS[moduleKey])}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                      <td style={{ padding: '12px', whiteSpace: 'nowrap' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '5px 12px',
                            backgroundColor: company.active_agreement_template_id ? '#10b981' : '#e5e7eb',
                            color: company.active_agreement_template_id ? '#fff' : '#374151',
                            borderRadius: '4px', fontSize: '12px', fontWeight: 600,
                          }}
                          title={company.active_agreement_template_id
                            ? `Live: ${company.active_agreement_template_name} — manage from Agreements`
                            : 'Nothing live yet — push a template from the Agreements page'}
                        >
                          {company.active_agreement_template_id ? '✓ On' : 'Off'}
                        </span>
                        <button
                          onClick={() => navigate(`/owner/agreements?company_id=${company.id}`)}
                          style={{
                            padding: '5px 10px', marginLeft: '6px',
                            backgroundColor: '#fff', color: '#374151',
                            border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                          }}
                          title="Upload / manage this company's introducer agreement templates"
                        >
                          Manage
                        </button>
                      </td>
                      <td style={{ padding: '12px', whiteSpace: 'nowrap' }}>
                        <button
                          onClick={() => handleEditTrial(company.id)}
                          style={{
                            padding: '6px 12px',
                            marginRight: '8px',
                            backgroundColor: '#f59e0b',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                          title="Set or change this company's free trial end date"
                        >
                          Trial
                        </button>
                        <button
                          onClick={() => handleEditRate(company.id)}
                          style={{
                            padding: '6px 12px',
                            marginRight: '8px',
                            backgroundColor: '#8b5cf6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                          title="Set a custom per-seat rate for this company, or clear it to use the platform default"
                        >
                          Rate
                        </button>
                        <button
                          onClick={() => navigate(`/owner/company/${company.id}`)}
                          style={{
                            padding: '6px 12px',
                            marginRight: '8px',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleDeleteCompany(company.id)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>
    </>
  );
}
